import { Children, type ReactNode } from 'react';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MarkdownContent } from '../MarkdownContent';
import type { DocumentSection } from '../../../core/types/data-models';

export interface DocumentSectionRowProps {
  section: DocumentSection;
  /** 0 for a root section; each level indents the title row and the body one step further. */
  depth?: number;
  open: boolean;
  onToggle: () => void;
  /**
   * Whether there is anything to reveal. Without it the row shows no chevron
   * and ignores clicks. Defaults to "has content or sub-section rows".
   */
  expandable?: boolean;
  /** Keeps an expandable row from toggling, e.g. while its content is edited inline. */
  toggleDisabled?: boolean;
  /** Fires on every title-row click, before any toggle (e.g. view-context tracking). */
  onTitleClick?: () => void;
  /** Rendered right after the title, inside its ellipsis cluster: status chips, badges. */
  titleAdornments?: ReactNode;
  /**
   * Rendered at the end of the title row: avatars, pills, buttons. Anything
   * here that must not toggle the row has to stop click propagation.
   */
  trailing?: ReactNode;
  /** Buttons of a toolbar that floats top-right of the row while its title is hovered. */
  toolbar?: ReactNode;
  /** Replaces the default Markdown rendering of `section.content`. */
  body?: ReactNode;
  /** Rendered beside the body inside the collapse, e.g. a comments panel. */
  aside?: ReactNode;
  /** The sub-section rows, rendered under the body at the next depth. */
  children?: ReactNode;
}

/**
 * One section of a document: the clickable title row (chevron, title, and
 * whatever the caller adds next to and after it) with the Markdown content
 * and the sub-section rows collapsed under it. This is the one presentation
 * of document content — the document page and the idea validation view both
 * build their rows on it and differ only in what they put in the slots.
 */
export function DocumentSectionRow({
  section,
  depth = 0,
  open,
  onToggle,
  expandable,
  toggleDisabled = false,
  onTitleClick,
  titleAdornments,
  trailing,
  toolbar,
  body,
  aside,
  children,
}: DocumentSectionRowProps) {
  const hasContent = !!section.content?.trim();
  const isExpandable = expandable ?? (hasContent || Children.count(children) > 0);
  const canToggle = isExpandable && !toggleDisabled;
  const chevronSx = { fontSize: depth === 0 ? 18 : 15, color: 'var(--color-muted)' };

  return (
    <Box
      sx={{
        position: 'relative',
        // show the hover toolbar only when mousing over this row's own title
        // (direct-child selectors so nested sub-section rows don't light up too)
        '& > .sec-title-row:hover + .sec-toolbar, & > .sec-toolbar:hover': {
          opacity: 1,
          transform: 'none',
        },
      }}
    >
      {/* ── Title row ── */}
      <Stack
        className="sec-title-row"
        direction="row"
        alignItems="center"
        onClick={() => {
          onTitleClick?.();
          if (canToggle) onToggle();
        }}
        sx={{
          pl: depth * 2.5,
          py: depth === 0 ? 1.25 : 0.75,
          borderRadius: 1,
          cursor: canToggle ? 'pointer' : 'default',
          '&:hover': canToggle ? { bgcolor: 'action.hover' } : {},
        }}
      >
        <Box sx={{ width: 24, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isExpandable && (
            // A real button so the keyboard reaches the toggle: Enter/Space
            // click it, and the click bubbles up to the row's handler.
            <Box
              component="button"
              type="button"
              aria-label={open ? 'Collapse section' : 'Expand section'}
              aria-expanded={open}
              disabled={toggleDisabled}
              sx={{
                display: 'flex',
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                font: 'inherit',
                cursor: 'inherit',
                borderRadius: 0.5,
                outlineOffset: 2,
              }}
            >
              {open ? <ExpandMoreIcon sx={chevronSx} /> : <ChevronRightIcon sx={chevronSx} />}
            </Box>
          )}
        </Box>

        {/* Title + whatever sits next to it — pinned together, ellipsised as one */}
        <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0, gap: 0.75, overflow: 'hidden' }}>
          <Typography
            component={depth === 0 ? 'h2' : depth === 1 ? 'h3' : 'h4'}
            sx={{
              flexShrink: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-sans)',
              fontSize: depth === 0 ? 'var(--font-size-xl)' : depth === 1 ? 16 : 'var(--font-size-base)',
              fontWeight: depth === 0 ? 700 : 600,
              letterSpacing: depth === 0 ? '-0.015em' : 'normal',
              lineHeight: 'var(--line-height-tight)',
              color: 'var(--color-ink)',
            }}
          >
            {section.title}
          </Typography>
          {titleAdornments}
        </Stack>

        {trailing}
      </Stack>

      {/* ── Hover toolbar (appears on section hover) ── */}
      {toolbar && (
        <Box
          className="sec-toolbar"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 4,
            right: 0,
            opacity: 0,
            transform: 'translateY(-3px)',
            transition: 'opacity 0.14s, transform 0.14s',
            display: 'flex',
            alignItems: 'center',
            gap: '1px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: '2px',
            boxShadow: 2,
            zIndex: 10,
          }}
        >
          {toolbar}
        </Box>
      )}

      {/* ── Collapsible content ── */}
      <Collapse in={open} unmountOnExit>
        <Stack direction="row" alignItems="flex-start" spacing={0}>
          <Box sx={{ flex: 1, pl: depth * 2.5 + 3, minWidth: 0 }}>
            {body !== undefined
              ? body
              : hasContent && <MarkdownContent sx={{ py: 1, pr: 2 }}>{section.content!}</MarkdownContent>}
            {children}
          </Box>
          {aside}
        </Stack>
      </Collapse>
    </Box>
  );
}
